'use client';

import React, { Component, ErrorInfo } from 'react';
import { useApiError } from '@/src/hooks/useApi';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  position: { left: number; top: number } | null;
}

type GlobalErrorPayload = {
  code: string;
  message: string;
  details?: unknown;
};

class ErrorBoundaryComponent extends Component<ErrorBoundaryProps & {
  globalError: GlobalErrorPayload | null;
  clearError: () => void;
}, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    position: null,
  };

  private errorContainerRef = React.createRef<HTMLDivElement>();
  private dragOffset: { x: number; y: number } | null = null;

  handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('details')) return;

    const container = this.errorContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    this.setState({ position: { left: rect.left, top: rect.top } });
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  handleDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!this.dragOffset) return;

    const container = this.errorContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const left = Math.min(
      Math.max(0, event.clientX - this.dragOffset.x),
      Math.max(0, window.innerWidth - rect.width),
    );
    const top = Math.min(
      Math.max(0, event.clientY - this.dragOffset.y),
      Math.max(0, window.innerHeight - rect.height),
    );

    this.setState({ position: { left, top } });
  };

  handleDragEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    this.dragOffset = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      position: null,
    };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps & { globalError: GlobalErrorPayload | null; clearError: () => void }) {
    if (!prevProps.globalError && this.props.globalError) {
      this.setState({ hasError: true });
    }

    if (prevProps.globalError && !this.props.globalError) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Boundary caught error:', error, errorInfo);
  }

  handleClose = () => {
    this.setState({ hasError: false, error: null });
    this.props.clearError();
  };

  renderFallback(errorData: GlobalErrorPayload | null) {
    const errorToRender = errorData || {
      code: 'RENDER_ERROR',
      message: this.state.error?.message || 'Something went wrong.',
      details: undefined,
    };

    const hasDetails = errorToRender.details !== undefined;
    const serializedDetails = hasDetails
      ? JSON.stringify(errorToRender.details, null, 2)
      : undefined;

    return (
      <div
        ref={this.errorContainerRef}
        className={styles.errorContainer}
        style={this.state.position ?? undefined}
      >
        <div className={styles.errorBox}>
          <div
            className={styles.errorHeader}
            onPointerDown={this.handleDragStart}
            onPointerMove={this.handleDragMove}
            onPointerUp={this.handleDragEnd}
            onPointerCancel={this.handleDragEnd}
          >
            <span className={styles.errorTitle}>Error</span>
            <button
              className={styles.closeButton}
              onClick={this.handleClose}
              aria-label="Close error message"
            >
              ×
            </button>
          </div>
          <div className={styles.errorBody}>
            <p className={styles.errorCode}>{errorToRender.code}</p>
            <p className={styles.errorMessage}>{errorToRender.message}</p>
            {hasDetails && serializedDetails && (
              <details className={styles.errorDetails}>
                <summary>Details</summary>
                <pre>{serializedDetails}</pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const shouldShowFallback = this.state.hasError || Boolean(this.props.globalError);

    if (shouldShowFallback) {
      return (
        <>
          {this.renderFallback(this.props.globalError)}
          {this.props.children}
        </>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { globalError, clearError } = useApiError();

  return (
    <ErrorBoundaryComponent globalError={globalError} clearError={clearError}>
      {children}
    </ErrorBoundaryComponent>
  );
};
